
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import { formatDate } from '@/utils/index'
import { exportTxt2Zip } from '@/utils/zip'
import {
  CreateOrg,
  UpdateOrgName,
  EnableOrg,
  DisableOrg,
  UpdateUserPassword,
  ListOrg
} from '@/api/superAdmin'
import { UserModule } from '@/store/modules/user'

import { RecordStatus, IOrg } from '@/api/types'

import Pagination from '@/components/Pagination/index.vue'

@Component({
  name: 'SuperAdminOrgs',
  components: {
    Pagination
  }
})
export default class extends Vue {
  public orgList: IOrg[] = []
  public total = 0
  public listLoading = true
  public inputKeyword = ''

  public orgQuery = {
    pageNum: 1,
    pageSize: 50,
    keywords: [] as string[],
    status: undefined
  }

  public statusOptions = [
    { label: '全部', value: undefined },
    { label: '启用', value: [RecordStatus.Enabled] },
    { label: '禁用', value: [RecordStatus.Disabled] }
  ]

  // 创建租户相关
  public createDialogVisible = false
  public createForm = {
    name: '',
  }

  public createRules = {
    name: [
      { required: true, message: '请输入租户名称', trigger: 'blur' },
      { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ]
  }

  // 管理员信息相关
  public adminInfoDialogVisible = false
  public adminInfo = {
    orgName: '',
    username: '',
    password: ''
  }

  // 编辑租户相关
  public updateDialogVisible = false
  public updateForm = {
    id: 0,
    orgName: ''
  }

  public updateRules = {
    orgName: [
      { required: true, message: '请输入租户名称', trigger: 'blur' },
      { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ]
  }

  // 修改密码相关
  public passwordDialogVisible = false
  public passwordForm = {
    id: 0,
    password: '',
  }

  public passwordRules = {
    password: [
      { required: true, message: '请输入新密码', trigger: 'blur' },
      { min: 6, max: 20, message: '长度在 6 到 20 个字符', trigger: 'blur' }
    ]
  }

  created() {
    this.getOrgList()
  }

  public async getOrgList() {
    this.listLoading = true
    try {
      const { data } = await ListOrg(this.orgQuery)
      this.orgList = data.items
      this.total = data.total
      // console.log(this.list)
    } catch (error) {
      console.error('获取租户列表失败:', error)
      this.$message.error('获取租户列表失败')
    } finally {
      this.listLoading = false
    }
  }

  public handleFilter() {
    this.orgQuery.pageNum = 1
    this.getOrgList()
  }

  public handleAddKeyword() {
    const keyword = this.inputKeyword.trim()
    if (keyword && !this.orgQuery.keywords.includes(keyword)) {
      this.orgQuery.keywords.push(keyword)
      this.inputKeyword = ''
    }
  }

  public handleRemoveKeyword(index: number) {
    this.orgQuery.keywords.splice(index, 1)
  }

  public formatTime(time: string) {
    if (!time) return ''
    return formatDate(time, 'YYYY-MM-DD HH:mm:ss')
  }

  // 创建租户相关方法
  public handleCreate() {
    this.resetCreateForm()
    this.createDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.createForm as ElForm).clearValidate()
    })
  }

  public resetCreateForm() {
    this.createForm = {
      name: '',
    }
  }

  public createData() {
    (this.$refs.createForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          const { data } = await CreateOrg({ name: this.createForm.name })
          this.createDialogVisible = false
          this.$message.success('创建租户成功')

          // 显示管理员信息
          this.adminInfo = {
            orgName: data.org.name,
            username: data.user.username,
            password: data.user.password
          }
          this.adminInfoDialogVisible = true

          // 刷新列表
          this.getOrgList()
        } catch (error) {
          console.error('创建租户失败:', error)
          this.$message.error('创建租户失败')
        }
      }
    })
  }

  public exportAdminInfo() {
    const header = ['信息项', '内容']
    const data = [
      ['租户名称', this.adminInfo.orgName],
      ['管理员用户名', this.adminInfo.username],
      ['管理员密码', this.adminInfo.password],
    ]
    exportTxt2Zip(header, data, '管理员信息', `租户管理员信息_${this.adminInfo.orgName}`)
    this.$message.success('管理员信息已导出')
  }

  // 编辑租户相关方法
  public handleUpdate(row: IOrg) {
    this.updateForm = {
      id: row.id,
      orgName: row.name
    }
    this.updateDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.updateForm as ElForm).clearValidate()
    })
  }

  public async updateData() {
    (this.$refs.updateForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await UpdateOrgName({ id: this.updateForm.id, orgName: this.updateForm.orgName })
          this.updateDialogVisible = false
          this.$message.success('更新租户名称成功')
          this.getOrgList()
        } catch (error) {
          console.error('更新租户名称失败:', error)
          this.$message.error('更新租户名称失败')
        }
      }
    })
  }

  // 启用/禁用租户相关方法
  public async handleEnable(row: IOrg) {
    try {
      await this.$confirm('确定要启用该租户吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })

      await EnableOrg({ id: row.id })
      this.$message.success('启用租户成功')
      this.getOrgList()
    } catch (error) {
      if (error !== 'cancel') {
        console.error('启用租户失败:', error)
        this.$message.error('启用租户失败')
      }
    }
  }

  public async handleDisable(row: IOrg) {
    try {
      await this.$confirm('确定要禁用该租户吗? 禁用后该租户下的所有用户将无法登录', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })

      await DisableOrg({ id: row.id })
      this.$message.success('禁用租户成功')
      this.getOrgList()
    } catch (error) {
      if (error !== 'cancel') {
        console.error('禁用租户失败:', error)
        this.$message.error('禁用租户失败')
      }
    }
  }

  // 修改管理员密码相关方法
  public handleUpdatePassword(row: IOrg) {
    this.passwordForm = {
      id: row.realAdminId,
      password: ''
    }
    this.passwordDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.passwordForm as ElForm).clearValidate()
    })
  }

  public async updatePassword() {
    (this.$refs.passwordForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await UpdateUserPassword({ id: this.passwordForm.id, password: this.passwordForm.password })
          this.passwordDialogVisible = false
          this.$message.success('修改管理员密码成功')
        } catch (error) {
          console.error('修改管理员密码失败:', error)
          this.$message.error('修改管理员密码失败')
        }
      }
    })
  }

  // 登录到租户相关方法
  public async handleLogin(row: IOrg) {
    try {
      await this.$confirm('确定要切换登录到该租户吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      })

      const path = await UserModule.SwitchOrg(row.id)
      this.$message.success(`已切换到租户: ${row.name}`)
      this.$router.push(path)
    } catch (error) {
      if (error !== 'cancel') {
        console.error('切换租户失败:', error)
        this.$message.error('切换租户失败')
      }
    }
  }
}
